import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Lookups, Gate, Status, Team, Priority, ItemType, ProofType, Module } from '../types';
import api from '../services/api';
import { useAuth } from './AuthContext';

interface LookupsContextType {
  lookups: Lookups | null;
  isLoading: boolean;
  error: string | null;
  gates: Gate[];
  statuses: Status[];
  teams: Team[];
  priorities: Priority[];
  itemTypes: ItemType[];
  proofTypes: ProofType[];
  modules: Module[];
  getGateName: (gateNumber: number) => string;
  getStatusName: (code: string) => string;
  getStatusColor: (code: string) => string;
  getTeamName: (code: string) => string;
  getPriorityName: (code: string) => string;
  refresh: () => Promise<void>;
}

const LookupsContext = createContext<LookupsContextType | undefined>(undefined);

export const LookupsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [lookups, setLookups] = useState<Lookups | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLookups = useCallback(async () => {
    console.log('LookupsContext - loadLookups called, isAuthenticated:', isAuthenticated);
    if (!isAuthenticated) return;

    setIsLoading(true);
    setError(null);
    try {
      console.log('LookupsContext - fetching lookups...');
      const data = await api.getLookups();
      console.log('LookupsContext - lookups loaded:', data);
      console.log('LookupsContext - teams count:', data.teams?.length);
      setLookups(data);
    } catch (err) {
      setError('Failed to load lookup data');
      console.error('Failed to load lookups:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  const getGateName = (gateNumber: number): string => {
    const gate = lookups?.gates.find((g) => g.gateNumber === gateNumber);
    return gate?.name || `Gate ${gateNumber}`;
  };

  const getStatusName = (code: string): string => {
    const status = lookups?.statuses.find((s) => s.code === code);
    return status?.name || code;
  };

  const getStatusColor = (code: string): string => {
    const status = lookups?.statuses.find((s) => s.code === code);
    return status?.color || '#999999';
  };

  const getTeamName = (code: string): string => {
    const team = lookups?.teams.find((t) => t.code === code);
    return team?.name || code;
  };

  const getPriorityName = (code: string): string => {
    const priority = lookups?.priorities.find((p) => p.code === code);
    return priority?.name || code;
  };

  return (
    <LookupsContext.Provider
      value={{
        lookups,
        isLoading,
        error,
        gates: lookups?.gates || [],
        statuses: lookups?.statuses || [],
        teams: lookups?.teams || [],
        priorities: lookups?.priorities || [],
        itemTypes: lookups?.itemTypes || [],
        proofTypes: lookups?.proofTypes || [],
        modules: lookups?.modules || [],
        getGateName,
        getStatusName,
        getStatusColor,
        getTeamName,
        getPriorityName,
        refresh: loadLookups,
      }}
    >
      {children}
    </LookupsContext.Provider>
  );
};

export const useLookups = (): LookupsContextType => {
  const context = useContext(LookupsContext);
  if (!context) {
    throw new Error('useLookups must be used within a LookupsProvider');
  }
  return context;
};
